// Authenticated landing page — project list with bulk-delete + show-all toggle.
// Merged from ProjectList.jsx; /projects route has been removed.

import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth, projectApi, formatApiError } from "../lib/auth";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { Checkbox } from "../components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { Plus, Copy, Trash2, Loader2 } from "lucide-react";
import AppHeader from "../components/AppHeader";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// Relative-time formatter — no external dependency.
// ---------------------------------------------------------------------------
function formatRelative(dateInput) {
  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);
  const diffWk = Math.floor(diffDay / 7);
  const diffMo = Math.floor(diffDay / 30);

  if (diffSec < 60) return "just now";
  if (diffMin < 60) return diffMin === 1 ? "1 minute ago" : `${diffMin} minutes ago`;
  if (diffHr < 24) return diffHr === 1 ? "1 hour ago" : `${diffHr} hours ago`;
  if (diffDay === 1) return "yesterday";
  if (diffDay < 7) return `${diffDay} days ago`;
  if (diffWk < 5) return diffWk === 1 ? "last week" : `${diffWk} weeks ago`;
  if (diffMo < 12) return diffMo === 1 ? "last month" : `${diffMo} months ago`;
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// ---------------------------------------------------------------------------
// Skeleton rows shown while loading
// ---------------------------------------------------------------------------
function SkeletonRows() {
  return Array.from({ length: 5 }).map((_, i) => (
    <TableRow key={i} className="hover:bg-transparent">
      <TableCell className="w-8" />
      <TableCell>
        <Skeleton className="h-4 w-48" />
      </TableCell>
      <TableCell>
        <Skeleton className="h-4 w-24" />
      </TableCell>
      <TableCell className="w-16" />
    </TableRow>
  ));
}

// ---------------------------------------------------------------------------
// Home page
// ---------------------------------------------------------------------------
const MAX_RECENT = 5;

export default function Home() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [busy, setBusy] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    projectApi
      .list()
      .then(setProjects)
      .catch((err) => toast.error("Could not load projects: " + formatApiError(err)))
      .finally(() => setBusy(false));
  }, [user]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login" replace />;

  const displayed = showAll ? projects : projects.slice(0, MAX_RECENT);
  const hasMore = projects.length > MAX_RECENT;
  const openProject = (id) => navigate(`/wizard?projectId=${id}`);
  const newProject = () => navigate("/wizard");

  // --- single row actions ---
  const cloneProject = async (id, name) => {
    try {
      const data = await projectApi.clone(id);
      setProjects((items) => [...items, data]);
      toast.success(`Project '${name}' cloned`);
    } catch (err) {
      toast.error("Clone failed: " + formatApiError(err));
    }
  };

  const deleteProject = async (id, name) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Delete "${name}"? This can't be undone.`)) return;
    try {
      await projectApi.remove(id);
      setProjects((items) => items.filter((p) => p.id !== id));
      setSelected((prev) => { const next = new Set(prev); next.delete(id); return next; });
      toast.success("Project deleted");
    } catch (err) {
      toast.error("Delete failed: " + formatApiError(err));
    }
  };

  // --- checkbox helpers ---
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allChecked = displayed.length > 0 && displayed.every((p) => selected.has(p.id));
  const someChecked = displayed.some((p) => selected.has(p.id)) && !allChecked;
  const toggleAll = () => {
    if (allChecked) {
      setSelected((prev) => {
        const next = new Set(prev);
        displayed.forEach((p) => next.delete(p.id));
        return next;
      });
    } else {
      setSelected((prev) => {
        const next = new Set(prev);
        displayed.forEach((p) => next.add(p.id));
        return next;
      });
    }
  };

  // --- bulk delete ---
  const bulkDelete = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Delete ${ids.length} project${ids.length === 1 ? "" : "s"}? This can't be undone.`)) return;
    setBulkBusy(true);
    const failures = [];
    await Promise.all(ids.map(async (id) => {
      try { await projectApi.remove(id); }
      catch (e) { failures.push({ id, error: formatApiError(e) }); }
    }));
    setBulkBusy(false);
    const succeeded = new Set(ids.filter((id) => !failures.some((f) => f.id === id)));
    setProjects((items) => items.filter((p) => !succeeded.has(p.id)));
    setSelected(new Set());
    if (failures.length === 0) {
      toast.success(`Deleted ${ids.length} project(s)`);
    } else {
      toast.error(`${failures.length}/${ids.length} delete(s) failed`);
    }
  };

  return (
    <div className="container mx-auto py-6 px-4 xl:px-10 max-w-[1728px]" data-testid="home-page">
      <AppHeader title="Selectool" />

      {/* Projects section */}
      <div className="mb-8">
        {/* Section header row — title + primary actions */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="text-sm font-semibold text-foreground" data-testid="home-recent-heading">
            Recent projects
          </h2>
          <TooltipProvider delayDuration={150}>
            <div className="flex items-center gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate("/single")}
                    className="h-7 text-xs"
                    data-testid="home-quick-selection-btn"
                  >
                    Quick selection
                  </Button>
                </TooltipTrigger>
                <TooltipContent>One-off equipment selection, no project save</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    onClick={newProject}
                    className="h-7 text-xs bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="home-new-project-btn"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" />
                    New project
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Start a new multi-room project — wizard workflow</TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        </div>

        {/* Divider */}
        <div className="border-t border-border mb-0" />

        {/* Bulk-delete toolbar — visible when ≥1 row is checked */}
        {selected.size > 0 && (
          <div
            className="flex items-center gap-3 mt-2 mb-1 p-2 px-3 bg-slate-900 text-white rounded-md text-sm"
            data-testid="home-bulk-toolbar"
          >
            <span className="font-medium">
              {selected.size} project{selected.size === 1 ? "" : "s"} selected
            </span>
            <div className="flex-1" />
            <Button
              size="sm"
              variant="ghost"
              className="text-white hover:bg-slate-800"
              onClick={() => setSelected(new Set())}
            >
              Clear
            </Button>
            <Button
              size="sm"
              className="bg-rose-600 hover:bg-rose-700 text-white"
              disabled={bulkBusy}
              onClick={bulkDelete}
              data-testid="home-bulk-delete"
            >
              {bulkBusy
                ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                : <Trash2 className="h-4 w-4 mr-1" />}
              Delete Selected
            </Button>
          </div>
        )}

        {/* Table */}
        {busy ? (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-8" />
                <TableHead className="text-xs">Name</TableHead>
                <TableHead className="text-xs">Last edited</TableHead>
                <TableHead className="w-16" />
              </TableRow>
            </TableHeader>
            <TableBody>
              <SkeletonRows />
            </TableBody>
          </Table>
        ) : projects.length === 0 ? (
          /* Empty state */
          <div className="py-10 text-center" data-testid="home-empty-state">
            <p className="text-sm text-muted-foreground mb-3">
              No projects yet. Start your first one.
            </p>
            <Button
              size="sm"
              onClick={newProject}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              New project
            </Button>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-8">
                    <Checkbox
                      checked={allChecked ? true : (someChecked ? "indeterminate" : false)}
                      onCheckedChange={toggleAll}
                      data-testid="home-select-all"
                    />
                  </TableHead>
                  <TableHead className="text-xs font-medium">Name</TableHead>
                  <TableHead className="text-xs font-medium">Last edited</TableHead>
                  <TableHead className="w-16" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayed.map((p) => (
                  <TableRow
                    key={p.id}
                    className={`cursor-pointer hover:bg-muted/50 ${selected.has(p.id) ? "bg-muted/30" : ""}`}
                    onClick={() => openProject(p.id)}
                    data-testid={`home-project-row-${p.id}`}
                  >
                    <TableCell
                      className="w-8 py-2.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={selected.has(p.id)}
                        onCheckedChange={() => toggleOne(p.id)}
                        data-testid={`home-project-select-${p.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium text-sm py-2.5">
                      {p.name}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground py-2.5 tabular-nums">
                      {formatRelative(p.updated_at)}
                    </TableCell>
                    <TableCell
                      className="py-2.5 w-16 text-right space-x-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        onClick={() => cloneProject(p.id, p.name)}
                        data-testid={`clone-btn-${p.id}`}
                        title="Duplicate project"
                      >
                        <Copy className="h-4 w-4 text-slate-600" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        onClick={() => deleteProject(p.id, p.name)}
                        data-testid={`delete-btn-${p.id}`}
                        title="Delete project"
                      >
                        <Trash2 className="h-4 w-4 text-rose-600" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Show all / show recent toggle */}
            {hasMore && (
              <div className="mt-2 text-right">
                {showAll ? (
                  <button
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setShowAll(false)}
                    data-testid="home-show-recent"
                  >
                    Show recent only
                  </button>
                ) : (
                  <button
                    className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                    onClick={() => setShowAll(true)}
                    data-testid="home-show-all"
                  >
                    Show all {projects.length} projects
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
